<template>
  <div>
    <div :id="chartId" class="container-chart bg-chart">
      <h6 class="title-font mt-3" style="text-align: center">{{ config.titles[$locale] }}</h6>
      <div class="lds-ripple" v-if="isDataLoading">
        <div></div>
        <div></div>
      </div>
      <chart-error :dataLoadedError="dataLoadedError" @reload="reloadData"></chart-error>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3'
import d3Tip from 'd3-tip'
import moment from 'moment'
import momentDurationFormatSetup from 'moment-duration-format'
import { TimeChartView } from '../../utils/charts/TimeChartView'
import { utilsMixin, chartMixin } from '../../utils/mixins'

export default {
  name: 'TimeChart',
  dependencies: ['dataService'],
  mixins: [utilsMixin, chartMixin],
  data() {
    return {
      moment: moment,
      dataset: { items: [] },
      sortedDataset: { lines: [] },
      chart: null,
      loading: true,
      checked: [],
      savedStart: null,
      savedEnd: null,
      chartWidth: 1080,
      chartHeight: 275,
      gridOpacity: 0,
      dataLoadedError: false,
      selectedPeriod: new Array(2),
      dataRangeType: 'NEW',
    }
  },
  props: ['config', 'chartId', 'period', 'timeCharts', 'area', 'mode', 'previewDataset'],
  mounted() {
    this.dataLoadedError = false
    d3.tip = d3Tip

    this.previousConfig = JSON.parse(JSON.stringify(this.config))

    momentDurationFormatSetup(moment)

    this.chart = new TimeChartView(
      this.chartId,
      this.sortedDataset,
      this.config.type,
      {
        width: this.chartWidth,
        height: this.chartHeight,
      },
      {
        top: 8,
        right: 20,
        bottom: 30,
        left: 50,
      },
      this.$t,
      this.config,
    )

    this.chart.setMode(this.mode)

    this.chart.initContainer(this.chartId, this.config.sort_by)
    this.chart.setTimeChartsRef(this.timeCharts)

    if (this.mode != 'preview') {
      this.savedStart = this.period[0]
      this.savedEnd = this.period[1]
    } else {
      setTimeout(() => {
        //Set timeout for svg preloading
        this.displayUpdatedData(this.previewDataset)
      }, 200)
    }
  },
  components: {
    'chart-error': () => import('../utils/ChartErrorReload'),
  },
  beforeDestroy() {
    this.dataService.cancelDataRequest(this.currentRequestId)
  },
  watch: {
    config: {
      handler() {
        if (this.mode != 'preview') {
          if (this.hasToRequestNewData()) {
            this.savedStart = null
            this.savedEnd = null
            this.updateRangePeriod()
            this.loadConfigAndFetchData()
          } else {
            this.displaySortedData()
          }
          this.previousConfig = JSON.parse(JSON.stringify(this.config))
        } else {
          this.displayUpdatedData(this.previewDataset)
        }
      },
      immediate: true,
    },
    period: {
      handler(period) {
        if (period[0] != this.savedStart || period[1] != this.savedEnd) {
          this.updateRangePeriod()
          this.savedStart = this.period[0]
          this.savedEnd = this.period[1]
          this.loadConfigAndFetchData()
        }
      },
      immediate: false,
    },
    timeCharts: {
      handler() {
        if (this.chart) {
          this.chart.setTimeChartsRef(this.timeCharts)
        }
      },
      immediate: false,
    },
    area: {
      handler() {
        this.loadConfigAndFetchData()
      },
      immediate: true,
    },
    previewDataset: {
      handler() {
        if (this.mode == 'preview') {
          setTimeout(() => {
            this.displayUpdatedData(this.previewDataset)
          }, 200)
        }
      },
      immediate: false,
    },
  },
  methods: {
    loadConfigAndFetchData: function() {
      if (this.requestCount < 0) {
        this.requestCount = 0
      }
      this.requestCount++

      if (this.dataRangeType == 'ALREADY_PRESENT') {
        this.displayUpdatedData(this.dataset)
      } else {
        this.dataService
          .getChartData(
            this.config,
            this.selectedPeriod,
            this.dateFormat,
            this.getCurrentUser,
            this.currentRequestId,
            this.$props.area,
          )
          .then(data => {
            this.displayUpdatedData(data.result)
          })
          .catch(e => {
            if (e.message != 'cancel') {
              this.$log.error('TimeChart error', e)
              this.dataLoadedError = true
            }
            this.requestCount--
          })
        this.currentRequestId = this.getRequestId(
          this.config,
          this.selectedPeriod,
          this.dateFormat,
          this.getCurrentUser,
        )
      }
    },
    displayUpdatedData: function(dataResult) {
      if (this.chart) {
        this.chart.setTitles(this.config.titles[this.$locale])
        this.chart.chartId = this.chartId

        this.dataset = this.filterResult(dataResult, this.period)

        this.$emit('update', {
          chart_id: this.config.id,
          data: JSON.parse(JSON.stringify(this.dataset)),
        })
        this.displaySortedData()
        this.loading = false
        this.dataLoadedError = false
        this.requestCount--
      }
    },
    updateRangePeriod: function() {
      let rangeObject = this.checkNewRange(this.savedStart, this.savedEnd, this.period)
      this.selectedPeriod = rangeObject.selectedPeriod
      this.dataRangeType = rangeObject.dataRangeType
    },
    displaySortedData: function() {
      this.checked = []
      this.sortedDataset['unit'] = this.dataset.unit

      if (this.dataset && this.dataset.items && this.dataset.items.length > 0) {
        this.sortedDataset['lines'] = []
        //If there we have to display all data
        if (
          this.config.sort_by == undefined ||
          this.config.sort_by === 'all' ||
          Object.keys(this.dataset.items[0][this.config.sort_by]).length == 0
        ) {
          /**
           * Array.from(this.dataset.items, item => {
              return { date: item.date, value: item.value }
            }),
           */

          this.sortedDataset.lines.push({
            sort: 'all',
            items: this.dataset.items /*.filter(item => {
              return item.value != 0 && (this.dataset.dataValue === 'histopad_rating' || this.dataset.dataValue === 'visit_rating')
            })*/
              .map(item => {
                if (
                  (this.dataset.dataValue === 'histopad_rating' ||
                    this.dataset.dataValue === 'visit_rating') &&
                  item.value == 0
                ) {
                  return { date: item.date, value: null }
                } else {
                  return { date: item.date, value: item.value }
                }
              }),
          })
        } else {
          //Else we display data sorted by filter
          this.sortedDataset['lines'] = []

          for (let key in this.dataset.items[0][this.config.sort_by]) {
            if (key) {
              this.sortedDataset['lines'].push({ items: [] })
            }
          }

          //Split data per lines (example : one per site_id if the TimeChart's data is sorted by site_id)
          // eslint-disable-next-line
          this.dataset.items.forEach(item => {
            //Retrieve sorted values according to the selected sort by in chart's configurations
            let sortedValues = item[this.config.sort_by]

            for (let key in sortedValues) {
              //Line already set
              let foundLine = this.sortedDataset.lines.find(line => {
                return line && line.sort == key
              })

              //Or not
              if (!foundLine) {
                foundLine = { sort: key, items: [] }
                this.checked.push(key)
                // eslint-disable-next-line no-redeclare
                let index =
                  this.config.sort_by == 'site_id' ||
                  this.config.sort_by == 'site_location' ||
                  this.config.sort_by == 'language' ||
                  this.config.sort_by == 'product' ||
                  this.config.sort_by == 'version' ||
                  this.config.sort_by == 'player_version' ||
                  this.config.sort_by == 'os_version' ||
                  this.config.sort_by == 'os_build' ||
                  this.config.sort_by == 'os_name' ||
                  this.config.sort_by == 'device_vendor' ||
                  this.config.sort_by == 'device_model' ||
                  this.config.sort_by == 'bucket' ||
                  this.config.sort_by == 'site' ||
                  this.config.sort_by == 'warning_type'
                    ? this.configFilterHierarchy.filter_values[this.config.sort_by].indexOf(key)
                    : key
                this.sortedDataset.lines[index] = foundLine
              }

              foundLine.items.push({
                date: item.date,
                value: sortedValues[key],
              })
            }
          })

          if (this.config.sort_by === 'site_id') {
            this.sortedDataset.lines.sort((a, b) => {
              return Number(a.sort) - Number(b.sort)
            })
          }

          //Remove line if there is no sort category found
          this.sortedDataset.lines = this.sortedDataset.lines.filter(line => {
            return line.sort != undefined
          })
        }

        this.dataset['lines'] = JSON.parse(JSON.stringify(this.sortedDataset.lines))

        this.updateChartData(this.dataset)

        d3.select('#' + this.chartId + ' svg').remove()
        this.chart.initContainer(this.chartId)
        this.chart.initTooltip(this.otherCategories)

        this.updateChartPeriod()

        this.chart.draw()
        this.chart.setGridCharts(this.gridCharts)
        this.chart.displayGrid()

        this.$emit('ref', this.chart)
      }
    },
    updateChartData: function() {
      this.checked.push('all')

      //If chart already displayed
      if (
        this.chart.sortedDataset &&
        this.chart.sortedDataset.lines &&
        this.config.sort_by &&
        this.config.sort_by !== 'all' &&
        this.chart.checked.length > 0 &&
        this.chart.config &&
        this.chart.config.sort_by === this.config.sort_by
      ) {
        //update checked legends according if new categories appears and previous unchecked legends
        this.updateCheckedLegends()

        this.sortedDataset.lines = this.sortedDataset.lines.filter(line => {
          return this.chart.checked.indexOf(line.sort) > -1
        })
      } else {
        //Set a copy of checked legends
        this.chart.setChecked(JSON.parse(JSON.stringify(this.checked)))
        this.chart.setUnchecked([])
      }

      this.chart.setConfig(this.config)
      this.chart.setDataset(this.dataset)
      this.chart.setSortedDataset(this.sortedDataset)

      const yTitle = this.dataset.unit !== 'no' ? `(${this.getUnitLabel(this.dataset)})` : ''
      this.chart.setTitles(this.config.titles[this.$locale], '', yTitle)
    },
    updateChartPeriod: function() {
      const months = Math.round(
        this.moment
          .duration(
            this.moment(this.dataset.end, 'YYYY-MM-DD').diff(
              this.moment(this.dataset.start, 'YYYY-MM-DD', this.dateFormat),
            ),
          )
          .asMonths(),
      )

      const days = Math.round(
        this.moment
          .duration(
            this.moment(this.dataset.end, 'YYYY-MM-DD').diff(
              this.moment(this.dataset.start, 'YYYY-MM-DD', this.dateFormat),
            ),
          )
          .asDays(),
      )

      this.chart.setMonths(months)
      this.chart.setDays(days)
    },
    reloadData: function() {
      if (this.dataLoadedError) {
        this.loadConfigAndFetchData()
        this.dataLoadedError = false
      }
    },
  },
}
</script>
